import React, { useState, useRef, useEffect } from 'react';

const ETAPAS = [
  { value: 'nuevo',      label: 'Nuevo',      color: '#6b7280' },
  { value: 'contactado', label: 'Contactado',  color: '#3b82f6' },
  { value: 'interesado', label: 'Interesado',  color: '#f59e0b' },
  { value: 'propuesta',  label: 'Propuesta',   color: '#8b5cf6' },
  { value: 'ganado',     label: 'Ganado',      color: '#10b981' },
  { value: 'perdido',    label: 'Perdido',     color: '#ef4444' },
];

const TIPO_ICONO = {
  llamada: '📞', whatsapp: '💬', email: '✉️', reunion: '🤝', otro: '📝',
};

const AVATAR_COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#be185d'];
const avatarColor = (str) => AVATAR_COLORS[(str || '?').charCodeAt(0) % AVATAR_COLORS.length];
const initials = (nombres, apellidos) => {
  const n = (nombres || '').trim()[0] || '';
  const a = (apellidos || '').trim()[0] || '';
  return (n + a).toUpperCase() || '?';
};

const limpiarTexto = (descripcion) => (descripcion || '')
  .replace(/^📱\s*(Primer contacto WA|Mensaje WA):\s*"?/, '')
  .replace(/^📤\s*Enviado:\s*"?/, '')
  .replace(/"$/, '');

const esMensajeWA = (s) => {
  const saliente = s.descripcion?.startsWith('📤 Enviado:');
  const entrante = s.tipo === 'whatsapp' && /^📱\s*(Primer contacto WA|Mensaje WA):/.test(s.descripcion || '');
  return { esChat: saliente || entrante, saliente };
};

const formatHora = (fecha) => new Date(fecha).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });

const formatDia = (fecha) => {
  const d = new Date(fecha);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(hoy.getDate() - 1);
  const mismoDia = (a, b) => a.toDateString() === b.toDateString();
  if (mismoDia(d, hoy)) return 'Hoy';
  if (mismoDia(d, ayer)) return 'Ayer';
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' });
};

const CrmChatPanel = ({
  prospecto, loading, esAdmin,
  onBack, showDetail, isMobile,
  onEdit, onVerFicha, onSeguimiento, onAgendar, onBloquear, onDelete,
  onEnviarMensaje, onNotaInterna,
}) => {
  const [mensaje, setMensaje] = useState('');
  const [modo, setModo] = useState('responder'); // 'responder' | 'nota'
  const [enviando, setEnviando] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const menuRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [prospecto?.id, prospecto?.seguimientos?.length]);

  useEffect(() => { setMensaje(''); setModo('responder'); }, [prospecto?.id]);

  const handleEnviar = async () => {
    if (!mensaje.trim() || enviando) return;
    setEnviando(true);
    try {
      if (modo === 'nota') {
        await onNotaInterna(mensaje.trim());
      } else {
        await onEnviarMensaje(mensaje.trim());
      }
      setMensaje('');
    } finally {
      setEnviando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  if (!loading && !prospecto) {
    return (
      <div style={{
        flex: 1,
        display: isMobile && !showDetail ? 'none' : 'flex',
        flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#e5ddd5', color: '#8a8580', gap: '0.75rem',
      }}>
        <span style={{ fontSize: '3rem' }}>💬</span>
        <p style={{ fontSize: '0.9rem', margin: 0, fontWeight: '500', color: '#64748b' }}>
          Selecciona una conversación
        </p>
        <p style={{ fontSize: '0.8rem', margin: 0, color: '#94a3b8' }}>
          Los mensajes de WhatsApp aparecen automáticamente
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e5ddd5' }}>
        <div className="loading" />
      </div>
    );
  }

  const etapaInfo = ETAPAS.find(e => e.value === prospecto.etapa) || { label: prospecto.etapa, color: '#6b7280' };
  const nombreCompleto = `${prospecto.nombres}${prospecto.apellidos ? ' ' + prospecto.apellidos : ''}`;
  const avatarBg = avatarColor(prospecto.nombres);
  const ini = initials(prospecto.nombres, prospecto.apellidos);

  const accionesSecundarias = [
    { label: '✏️ Editar', onClick: onEdit, show: true },
    { label: '📋 Ficha', onClick: onVerFicha, show: true },
    { label: '🚫 Bloquear', onClick: onBloquear, show: !!prospecto.whatsapp_number && esAdmin },
    { label: '🗑️ Eliminar', onClick: onDelete, show: esAdmin && !!onDelete, danger: true },
  ].filter(a => a.show);

  // Timeline: mensajes WA + notas internas, orden cronológico ascendente
  const timeline = [...(prospecto.seguimientos || [])].reverse();
  let diaAnterior = null;

  return (
    <div style={{
      flex: 1,
      display: isMobile && !showDetail ? 'none' : 'flex',
      flexDirection: 'column',
      background: '#e5ddd5',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      {/* ── Header ── */}
      <div style={{
        background: 'white', borderBottom: '1px solid #e2e8f0',
        padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
        flexShrink: 0,
      }}>
        {isMobile && (
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#374151', flexShrink: 0 }}>←</button>
        )}
        <div style={{
          width: '38px', height: '38px', flexShrink: 0, borderRadius: '50%',
          background: avatarBg, color: 'white', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontWeight: '700', fontSize: '0.82rem',
        }}>{ini}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: '0 0 0.15rem', fontSize: '0.92rem', fontWeight: '700', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {nombreCompleto}
          </h2>
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.72rem', color: '#94a3b8' }}>
            {prospecto.whatsapp_number && <span>💬 {prospecto.whatsapp_number}</span>}
            <span style={{ background: etapaInfo.color + '18', color: etapaInfo.color, borderRadius: '999px', padding: '0.05rem 0.5rem', fontWeight: '700' }}>{etapaInfo.label}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexShrink: 0 }}>
          <button onClick={onSeguimiento} title="Agregar seguimiento" style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>+ Seguimiento</button>
          <button onClick={onAgendar} title="Agendar cita" style={{ background: '#f0fdf4', color: '#16a34a', border: '1.5px solid #86efac', borderRadius: '8px', padding: '0.4rem 0.7rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>📅 Agendar</button>
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button onClick={() => setMenuAbierto(v => !v)} title="Más acciones" style={{ background: menuAbierto ? '#f1f5f9' : '#f8fafc', color: '#374151', border: '1.5px solid #e2e8f0', borderRadius: '8px', padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '1rem', fontWeight: '700', lineHeight: 1 }}>⋯</button>
            {menuAbierto && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', minWidth: '180px', zIndex: 100, overflow: 'hidden' }}>
                {accionesSecundarias.map((a, i) => (
                  <button key={i} onClick={() => { a.onClick(); setMenuAbierto(false); }}
                    style={{ width: '100%', padding: '0.65rem 1rem', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.83rem', fontWeight: '500', color: a.danger ? '#dc2626' : '#374151', borderBottom: i < accionesSecundarias.length - 1 ? '1px solid #f1f5f9' : 'none', display: 'block' }}
                    onMouseEnter={e => e.currentTarget.style.background = a.danger ? '#fef2f2' : '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >{a.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Timeline ── */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '1.25rem',
        backgroundColor: '#e5ddd5',
        backgroundImage: 'radial-gradient(rgba(0,0,0,0.045) 1.2px, transparent 1.2px)',
        backgroundSize: '16px 16px',
      }}>
        {timeline.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 0', color: '#94a3b8' }}>
            <div style={{ fontSize: '1.75rem', marginBottom: '0.4rem' }}>📭</div>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>Sin mensajes ni notas todavía</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxWidth: '640px', margin: '0 auto' }}>
            {timeline.map(s => {
              const { esChat, saliente } = esMensajeWA(s);
              const dia = formatDia(s.fecha_contacto);
              const mostrarDivisor = dia !== diaAnterior;
              diaAnterior = dia;

              const divisor = mostrarDivisor && (
                <div key={`dia-${s.id}`} style={{ display: 'flex', justifyContent: 'center', margin: '0.75rem 0' }}>
                  <span style={{ background: '#fff', color: '#64748b', fontSize: '0.68rem', fontWeight: '600', padding: '0.2rem 0.7rem', borderRadius: '999px', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}>{dia}</span>
                </div>
              );

              if (!esChat) {
                // Nota interna / seguimiento manual
                return (
                  <React.Fragment key={s.id}>
                    {divisor}
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '0.3rem 0' }}>
                      <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.5rem 0.85rem', maxWidth: '85%', textAlign: 'center' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#92400e', marginBottom: '0.15rem' }}>
                          {TIPO_ICONO[s.tipo] || '📝'} {s.usuario_nombre?.trim() || 'Nota'} · {formatHora(s.fecha_contacto)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#78350f' }}>{s.descripcion}</div>
                        {s.fecha_proximo && (
                          <div style={{ marginTop: '0.2rem', fontSize: '0.68rem', color: '#b45309', fontWeight: '600' }}>
                            ⏰ Próximo: {new Date(s.fecha_proximo).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              }

              const texto = limpiarTexto(s.descripcion);
              return (
                <React.Fragment key={s.id}>
                  {divisor}
                  <div style={{ display: 'flex', justifyContent: saliente ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%', padding: '0.5rem 0.75rem', borderRadius: '10px',
                      background: saliente ? '#dcf8c6' : 'white',
                      boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                      fontSize: '0.85rem', color: '#111827', lineHeight: 1.4,
                    }}>
                      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{texto}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.25rem', marginTop: '0.2rem' }}>
                        <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{formatHora(s.fecha_contacto)}</span>
                        {saliente && <span style={{ fontSize: '0.7rem', color: '#34b7f1' }}>✓✓</span>}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* ── Caja de respuesta ── */}
      {prospecto?.whatsapp_number && onEnviarMensaje && (
        <div style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', background: 'white' }}>
          <div style={{ display: 'flex', gap: '0.25rem', padding: '0.5rem 1.25rem 0' }}>
            {[{ key: 'responder', label: '💬 Responder' }, { key: 'nota', label: '📝 Notas' }].map(t => (
              <button key={t.key} onClick={() => setModo(t.key)}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  padding: '0.4rem 0.75rem', fontSize: '0.78rem', fontWeight: '600',
                  color: modo === t.key ? '#2563eb' : '#94a3b8',
                  borderBottom: modo === t.key ? '2px solid #2563eb' : '2px solid transparent',
                }}
              >{t.label}</button>
            ))}
          </div>
          <div style={{ padding: '0.6rem 1.25rem 0.85rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-end' }}>
            <textarea
              value={mensaje}
              onChange={e => setMensaje(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={modo === 'nota' ? 'Escribe una nota (no se envía al contacto)...' : 'Mensaje de WhatsApp... (Enter para enviar)'}
              rows={2}
              style={{
                flex: 1, border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '0.6rem 0.75rem',
                fontSize: '0.875rem', resize: 'none', outline: 'none',
                backgroundColor: modo === 'nota' ? '#fffbeb' : '#f8fafc', transition: 'border-color 0.15s',
                boxSizing: 'border-box', fontFamily: 'inherit',
              }}
              onFocus={e => { e.target.style.borderColor = modo === 'nota' ? '#fbbf24' : '#93c5fd'; }}
              onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
            />
            <button
              onClick={handleEnviar}
              disabled={!mensaje.trim() || enviando}
              style={{
                background: !mensaje.trim() || enviando ? '#e2e8f0' : modo === 'nota' ? '#f59e0b' : 'linear-gradient(135deg, #25d366, #128c7e)',
                color: mensaje.trim() && !enviando ? 'white' : '#94a3b8',
                border: 'none', borderRadius: '10px', width: '42px', height: '42px',
                cursor: mensaje.trim() && !enviando ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.1rem', flexShrink: 0, transition: 'all 0.15s',
              }}
            >{enviando ? '⏳' : '➤'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmChatPanel;
